// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import {
  Box,
  Card,
  Flex,
  Typography,
  useDesktopMediaQuery,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { TypographyProps } from '@iheartjane/reefer';
import { useRuntimeConfig } from '@iheartjane/runtime-config';
import { useLocation } from 'react-router';

import {
  JaneAuthentication,
  PageLayout,
  getCanonicalPath,
} from '@jane/shared-ecomm/components';
import type {
  JaneAuthenticationProps,
  SignInFormData,
} from '@jane/shared-ecomm/components';
import { useCurrentStoreId } from '@jane/shared-ecomm/hooks';
import LogInHeroImage from '@jane/shared/assets/logIn/log-in-hero.svg';
import { PageTitle } from '@jane/shared/components';
import { useStore } from '@jane/shared/data-access';
import { useEcommApp } from '@jane/shared/providers';
import { getEcommPaths } from '@jane/shared/util';

import { JaneGoldIcon } from './icons/JaneGoldIcon';
import { LiveMenusIcon } from './icons/LiveMenusIcon';
import { ReviewsIcon } from './icons/ReviewsIcon';

const HeroContainer = styled(Box)({
  backgroundImage: `url("${LogInHeroImage}")`,
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  overflow: 'hidden',
});

const TransparentTypography = styled(Typography)({
  opacity: 0.5,
});

const sharedTypographyProps = {
  branded: true,
  textAlign: 'center',
  color: 'grays-white',
  variant: 'header-bold',
} as Partial<TypographyProps>;

interface LogInProps extends Omit<
  JaneAuthenticationProps,
  'onAppleClick' | 'onGoogleClick' | 'onSignInSubmit'
> {
  onAppleClick: (source: string) => void;
  onGoogleClick: (source: string) => void;
  onSignInSubmit: (data: SignInFormData, source: string) => void;
}

export const LogIn = (authenticationProps: LogInProps) => {
  const { appMode, isIframe } = useEcommApp();
  const {
    isBoostForAll,
    isPartnerHosted,
    partnerHostedPath,
    isFramelessEmbedMode,
  } = useRuntimeConfig();
  const isMarketplace = appMode === 'default';
  const isMobile = useMobileMediaQuery({ size: 'lg', width: 'max' });
  const isDesktop = useDesktopMediaQuery({ width: 'min' });
  const { search } = useLocation();

  const storeId = useCurrentStoreId();
  const { data: store } = useStore(storeId || '', {
    enabled: Boolean(!!storeId && appMode !== 'default'),
  });

  const { onAppleClick, onGoogleClick, onSignInSubmit } = authenticationProps;

  const ecommPaths = getEcommPaths(
    {
      appMode,
      isBoostForAll,
      isPartnerHosted,
      partnerHostedPath,
      storePathProps: store
        ? { id: store?.id, name: store?.name, slug: store?.url_slug }
        : undefined,
    },
    search
  );

  const loginFunctions = {
    onAppleClick: () => onAppleClick('login page'),
    onGoogleClick: () => onGoogleClick('login page'),
    onSignInSubmit: (data: SignInFormData) =>
      onSignInSubmit(data, 'login page'),
  };

  const showMarketplaceCallout = isMarketplace && isDesktop;

  return (
    <>
      <PageTitle
        title="Let's check if you have an account"
        store={store}
        url={getCanonicalPath({
          inIframe: isIframe,
          isBoostWp: isFramelessEmbedMode && !isBoostForAll,
          pagePath: ecommPaths.login(),
        })}
      />
      <PageLayout as="main" id="content" offscreenTitle="Log In" fullScreen>
        <Flex width="100%" height="100%">
          {showMarketplaceCallout && (
            <HeroContainer width="50%" py={96}>
              <Box maxWidth={342} mx="auto">
                <Typography
                  my={24}
                  {...sharedTypographyProps}
                  variant="title-bold"
                >
                  Shop & discover cannabis your way
                </Typography>

                <JaneGoldIcon mt={48} mx="auto" />
                <Typography mt={16} {...sharedTypographyProps}>
                  Earn Jane Gold rewards from your favorite brands*
                </Typography>

                <LiveMenusIcon mt={48} mx="auto" />
                <Typography mt={16} {...sharedTypographyProps}>
                  Shop live menus for pickup & delivery
                </Typography>

                <ReviewsIcon mt={48} mx="auto" />
                <Typography mt={16} {...sharedTypographyProps}>
                  Reviews for over 1 million products
                </Typography>

                <TransparentTypography
                  branded
                  mt={48}
                  color="grays-white"
                  textAlign="center"
                  variant="mini"
                >
                  *Available in select markets
                </TransparentTypography>
              </Box>
            </HeroContainer>
          )}

          <Flex
            width={showMarketplaceCallout ? '50%' : '100%'}
            alignContent="center"
            justifyContent="center"
            py={showMarketplaceCallout ? 96 : isMobile ? 0 : 64}
          >
            <Card
              flat
              border={isMobile ? 'none' : 'grays-light'}
              width={isMobile ? '100%' : 450}
              my={isMobile ? 0 : 'auto'}
            >
              <Card.Content>
                <Box p={showMarketplaceCallout ? 24 : 8}>
                  <JaneAuthentication
                    {...authenticationProps}
                    {...loginFunctions}
                  />
                </Box>
              </Card.Content>
            </Card>
          </Flex>
        </Flex>
      </PageLayout>
    </>
  );
};
